import { useState } from 'react'
import { getNotes } from '../../api/notes'
import type { Note } from '../../api/notes'

interface AppendToNoteDialogProps {
  isOpen: boolean
  conversationId: string
  messageId: string
  onConfirm: (targetNoteId: string) => Promise<void>
  onClose: () => void
}

export default function AppendToNoteDialog({
  isOpen,
  onConfirm,
  onClose,
}: AppendToNoteDialogProps) {
  const [search, setSearch] = useState('')
  const [notes, setNotes] = useState<Note[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)
  const [isAppending, setIsAppending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen) return null

  async function handleSearch(q: string) {
    setSearch(q)
    if (!q.trim()) { setNotes([]); return }
    setIsSearching(true)
    try {
      const result = await getNotes({ search_keyword: q, view_status: 'active' }, 'modified_time', 'desc', 0, 20)
      setNotes(result.data)
    } catch {
      setNotes([])
    } finally {
      setIsSearching(false)
    }
  }

  async function handleConfirm() {
    if (!selectedNote) return
    setIsAppending(true)
    setError(null)
    try {
      await onConfirm(selectedNote.id)
      onClose()
    } catch {
      setError('Failed to append to note. Please try again.')
    } finally {
      setIsAppending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div data-testid="append-to-note-dialog" className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Append to Note</h3>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Search for a note to append the assistant's response to.
          </p>

          {/* Search */}
          <div className="relative mb-3">
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              data-testid="append-note-search"
              placeholder="Search notes by title…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Results */}
          <div className="max-h-52 overflow-y-auto border border-gray-200 rounded-lg mb-4">
            {isSearching ? (
              <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin mr-2" />
                Searching…
              </div>
            ) : notes.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-400">
                {search ? 'No notes found' : 'Type to search your notes'}
              </div>
            ) : (
              <ul>
                {notes.map((note) => (
                  <li
                    key={note.id}
                    data-testid="append-note-item"
                    onClick={() => setSelectedNote(note)}
                    className={`px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-0 transition-colors ${
                      selectedNote?.id === note.id
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <p className="text-sm font-medium truncate">{note.name}</p>
                    {note.content && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {note.content.replace(/<[^>]+>/g, '').slice(0, 80)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p data-testid="append-note-error" className="text-sm text-red-600 mb-3">{error}</p>}

          <div className="flex justify-end gap-3">
            <button
              data-testid="append-note-cancel"
              onClick={onClose}
              disabled={isAppending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              data-testid="append-note-confirm"
              onClick={handleConfirm}
              disabled={!selectedNote || isAppending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-secondary disabled:opacity-50 flex items-center gap-2"
            >
              {isAppending && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              Append to "{selectedNote?.name.slice(0, 20) ?? '…'}"
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


